import { Link, NavLink } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { PHONE_TEL, WHATSAPP_URL, SYMPTOMS } from "../data/site";
import { DISEASE_CONTENT } from "../data/diseaseContent";
import { PROCEDURE_CONTENT } from "../data/procedureContent";

// Build dropdown items dynamically from data
const symptomChildren = SYMPTOMS.map((s) => ({
  label: s.title,
  to: `/symptoms/${s.slug}`,
}));
const conditionChildren = Object.entries(DISEASE_CONTENT).map(([slug, d]) => ({
  label: d.shortTitle,
  to: `/diseases/${slug}`,
}));
const procedureChildren = Object.entries(PROCEDURE_CONTENT).map(([slug, p]) => ({
  label: p.shortTitle,
  to: `/procedures/${slug}`,
}));

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/symptoms", label: "Symptoms", children: symptomChildren },
  { to: "/diseases", label: "Conditions", children: conditionChildren },
  { to: "/procedures", label: "Procedures", children: procedureChildren },
  {
    label: "Resources",
    children: [
      { label: "Blog", to: "/blog" },
      { label: "Videos", to: "/videos" },
      { label: "Testimonials", to: "/testimonials" },
    ],
  },
  { to: "/contact", label: "Contact" },
];

function DesktopMenuItem({ item }) {
  const [open, setOpen] = useState(false);
  const timer = useRef();
  const wrap = useRef(null);

  const onEnter = () => {
    clearTimeout(timer.current);
    if (item.children) setOpen(true);
  };
  const onLeave = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        data-testid={`nav-${item.label.toLowerCase()}`}
        className={({ isActive }) =>
          `text-[15px] font-medium tracking-wide transition-colors ${
            isActive ? "text-[#0370AD]" : "text-black/75 hover:text-black"
          }`
        }
      >
        {item.label}
      </NavLink>
    );
  }

  return (
    <div
      ref={wrap}
      className="relative"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {item.to ? (
        <NavLink
          to={item.to}
          data-testid={`nav-${item.label.toLowerCase()}`}
          className={({ isActive }) =>
            `inline-flex items-center gap-1 text-[15px] font-medium tracking-wide transition-colors ${
              isActive ? "text-[#0370AD]" : "text-black/75 hover:text-black"
            }`
          }
        >
          {item.label}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </NavLink>
      ) : (
        <button
          type="button"
          data-testid={`nav-${item.label.toLowerCase()}`}
          className="inline-flex items-center gap-1 text-[15px] font-medium tracking-wide text-black/75 hover:text-black transition-colors"
          aria-haspopup="true"
          aria-expanded={open}
        >
          {item.label}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      )}

      {open && (
        <div
          data-testid={`dropdown-${item.label.toLowerCase()}`}
          className="absolute left-0 top-[calc(100%+10px)] z-50 min-w-[280px] bg-white border border-black/10 rounded-[6px] shadow-[0_18px_40px_-12px_rgba(0,0,0,0.18)] py-3"
        >
          {item.children.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`dropdown-item-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="block px-5 py-2.5 text-[14.5px] text-black/75 hover:text-[#0370AD] hover:bg-[#EAF6FD] transition-colors"
              onClick={() => setOpen(false)}
            >
              {c.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileMenuItem({ item, onNavigate }) {
  const [expanded, setExpanded] = useState(false);

  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        onClick={onNavigate}
        data-testid={`mobile-nav-${item.label.toLowerCase()}`}
        className={({ isActive }) =>
          `py-3 text-[16px] font-medium border-b border-black/5 block ${
            isActive ? "text-[#0370AD]" : "text-black/80"
          }`
        }
      >
        {item.label}
      </NavLink>
    );
  }

  return (
    <div className="border-b border-black/5">
      <div className="flex items-center justify-between">
        {item.to ? (
          <NavLink
            to={item.to}
            onClick={onNavigate}
            data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            className="py-3 text-[16px] font-medium text-black/80 flex-1"
          >
            {item.label}
          </NavLink>
        ) : (
          <span
            data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            className="py-3 text-[16px] font-medium text-black/80 flex-1"
          >
            {item.label}
          </span>
        )}
        <button
          type="button"
          aria-label={`toggle ${item.label}`}
          data-testid={`mobile-toggle-${item.label.toLowerCase()}`}
          onClick={() => setExpanded((v) => !v)}
          className="h-10 w-10 flex items-center justify-center"
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      </div>
      {expanded && (
        <div className="pb-2 pl-3 border-l-2 border-[#0370AD]/40 ml-1 mb-2">
          {item.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              onClick={onNavigate}
              className="block py-2 text-[14.5px] text-black/70 pl-3"
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 bg-white/90 backdrop-blur transition-shadow ${
        scrolled ? "shadow-[0_1px_0_0_rgba(0,0,0,0.08)]" : ""
      }`}
    >
      <div className="container-x flex items-center justify-between py-4 md:py-5">
        <Link to="/" data-testid="header-logo-link" className="flex flex-col leading-tight">
          <span className="font-serif-display text-[22px] md:text-[26px] tracking-tight">
            DR INDRESH DIXIT
          </span>
          <span className="text-[10px] md:text-[11px] tracking-[0.18em] uppercase text-black/55 font-medium">
            DM, Gastroenterology, Varanasi
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {NAV.map((n) => (
            <DesktopMenuItem key={n.label} item={n} />
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="header-book-appointment-btn"
            className="btn-primary text-[13px] !py-2.5 !px-4"
          >
            Book Appointment
          </a>
        </div>

        <button
          aria-label="menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-black/10 bg-white max-h-[80vh] overflow-y-auto">
          <div className="container-x py-4 flex flex-col">
            {NAV.map((n) => (
              <MobileMenuItem key={n.label} item={n} onNavigate={() => setOpen(false)} />
            ))}
            <div className="flex gap-3 pt-5">
              <a
                href={PHONE_TEL}
                className="btn-ghost flex-1 justify-center"
                data-testid="mobile-call-btn"
              >
                <Phone className="h-4 w-4" /> Call
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-primary flex-1 justify-center"
                data-testid="mobile-book-appointment-btn"
              >
                Book Appointment
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
